import React, { useState, useCallback } from 'react';
import { Box, Typography, IconButton, Collapse, Paper } from '@mui/material';
import { ExpandMore as ExpandMoreIcon, ContentCopy as CopyIcon } from '@mui/icons-material';
import { colors, typography } from '../styles/globals';

const JsonNode = ({ data, label, defaultExpanded = true, depth = 0 }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  if (data === null) return <span style={{ color: '#9c27b0' }}>null</span>;
  if (typeof data === 'boolean') return <span style={{ color: '#0288d1' }}>{String(data)}</span>;
  if (typeof data === 'number') return <span style={{ color: '#2e7d32' }}>{data}</span>;
  if (typeof data === 'string') return <span style={{ color: '#c62828' }}>"{data}"</span>;

  const isArray = Array.isArray(data);
  const entries = isArray ? data.map((v, i) => [i, v]) : Object.entries(data);
  const bracket = isArray ? ['[', ']'] : ['{', '}'];

  if (entries.length === 0) {
    return <span style={{ color: '#666' }}>{bracket[0]}{bracket[1]}</span>;
  }

  return (
    <span>
      <span
        onClick={() => setExpanded(!expanded)}
        style={{ cursor: 'pointer', userSelect: 'none' }}
      >
        <span style={{ color: '#999', fontSize: '0.7em', marginRight: 4 }}>
          {expanded ? '▼' : '▶'}
        </span>
        {label && <span style={{ color: '#1565c0' }}>{label}: </span>}
        {!expanded && (
          <span style={{ color: '#999' }}>
            {bracket[0]} {entries.length} {isArray ? 'items' : 'keys'} {bracket[1]}
          </span>
        )}
      </span>
      {expanded && (
        <span>
          {bracket[0]}
          <div style={{ paddingLeft: 20 }}>
            {entries.map(([key, value], i) => (
              <div key={key} style={{ lineHeight: 1.6 }}>
                {typeof value === 'object' && value !== null ? (
                  <JsonNode data={value} label={isArray ? `${key}` : `"${key}"`} depth={depth + 1} />
                ) : (
                  <span>
                    <span style={{ color: '#1565c0' }}>{isArray ? key : `"${key}"`}</span>
                    {': '}
                    <JsonNode data={value} depth={depth + 1} />
                  </span>
                )}
                {i < entries.length - 1 && ','}
              </div>
            ))}
          </div>
          {bracket[1]}
        </span>
      )}
    </span>
  );
};

const ExternalApiResponseSection = ({ data }) => {
  const [expanded, setExpanded] = useState(false);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
  }, [data]);

  if (!data) return null;

  return (
    <Box sx={{ mt: 2, width: '100%' }}>
      <Paper
        elevation={0}
        sx={{
          border: '1px solid #e0e0e0',
          borderRadius: '12px',
          overflow: 'hidden',
        }}
      >
        <Box
          onClick={() => setExpanded(!expanded)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 1.5,
            cursor: 'pointer',
            '&:hover': { backgroundColor: '#fafafa' },
          }}
        >
          <Typography
            sx={{
              fontFamily: typography.fontFamily,
              fontWeight: typography.fontWeights.semibold,
              fontSize: typography.fontSizes.h3,
              color: colors.primary,
            }}
          >
            API response
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {expanded && (
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); handleCopy(); }}
                title="Copy JSON"
              >
                <CopyIcon fontSize="small" />
              </IconButton>
            )}
            <ExpandMoreIcon
              sx={{
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s',
                color: '#666',
              }}
            />
          </Box>
        </Box>
        <Collapse in={expanded}>
          <Box
            sx={{
              px: 3,
              pb: 3,
              maxHeight: '70vh',
              overflow: 'auto',
              backgroundColor: '#fafafa',
              fontFamily: '"Roboto Mono", "Consolas", monospace',
              fontSize: '0.8rem',
              lineHeight: 1.6,
            }}
          >
            <JsonNode data={data} defaultExpanded={true} />
          </Box>
        </Collapse>
      </Paper>
    </Box>
  );
};

export default ExternalApiResponseSection;
